<script setup>
import { ref, onMounted } from 'vue';
import { useMediaQuery } from '@vueuse/core';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Navigation, Pagination } from 'swiper/modules';

import VideoPlayer from '@/components/ui/VideoPlayer.vue';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import '@/assets/styles/swiper-custom.scss';

import PhotoSwipeLightbox from 'photoswipe/lightbox';
import 'photoswipe/style.css';

defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  title: {
    type: String,
    default: () => '',
  },
  id: {
    type: String,
    default: () => '',
  },
  view: {
    type: String,
    default: () => 'default',
  },
  hasModeButtons: {
    type: Boolean,
    default: false,
  },
  backgroundColor: {
    type: String,
    default: () => '',
  },
});

const galleryEl = ref(null);
const isMobile = useMediaQuery('(max-width: 767px)');

onMounted(() => {
  const lightbox = new PhotoSwipeLightbox({
    gallery: galleryEl.value,
    children: 'a',
    pswpModule: () => import('photoswipe'),
  });
  lightbox.init();
});

const isLocked = ref(true);
const onSwiper = (swiper) => {
  isLocked.value = swiper.isLocked;
};

const onLockToggle = (swiper) => {
  isLocked.value = swiper.isLocked;
};
</script>

<template>
  <div
    ref="galleryEl"
    class="gallery"
    :class="[view === 'without-fraction' ? 'gallery--without-fraction' : '']"
  >
    <template v-if="isMobile">
      <Swiper
        :slides-per-view="'auto'"
        :space-between="16"
        :modules="[Pagination]"
        :pagination="{
          type: 'bullets',
          el: `#${id}-pagination`,
          clickable: true,
        }"
        class="gallery__swiper"
        data-aos="fade-up"
        data-aos-easing="ease-out-cubic"
        @swiper="onSwiper"
        @lock="onLockToggle"
        @unlock="onLockToggle"
      >
        <SwiperSlide
          v-for="(item, index) in items"
          :key="index"
          :class="[
            item.url ? 'gallery__item gallery__item--video' : 'gallery__item',
          ]"
          :style="
            item.url
              ? 'aspect-ratio: 16 / 9;'
              : `aspect-ratio: ${item.width / item.height}`
          "
        >
          <a
            v-if="item.src"
            :href="item.src"
            :data-pswp-width="item.width"
            :data-pswp-height="item.height"
            target="_blank"
            rel="noreferrer"
            class="gallery__item-link"
          >
            <NuxtImg
              v-if="item?.src"
              :src="item.src"
              :alt="item.alt"
              class="gallery__item-image"
              loading="lazy"
            />
          </a>
          <VideoPlayer
            v-else-if="item.url"
            :url="item.url"
            :preview="item.preview"
            :title="item.title"
            :view="'carousel'"
          />
        </SwiperSlide>
      </Swiper>

      <div
        v-show="!isLocked"
        class="gallery__bottom"
        :class="{ 'gallery__bottom--with-buttons': hasModeButtons }"
        data-aos="fade-up"
        data-aos-easing="ease-out-cubic"
      >
        <slot name="mode-buttons" />
        <div
          v-show="!isLocked"
          :id="`${id}-pagination`"
          class="swiper-pagination gallery__pagination"
        ></div>
      </div>
    </template>
    <template v-else>
      <Swiper
        :slides-per-view="'auto'"
        :space-between="16"
        :modules="[Pagination, Navigation]"
        :pagination="{
          type: 'fraction',
          el: `#${id}-pagination`,
          formatFractionCurrent: function (number) {
            return number < 10 ? '0' + number : number;
          },
          formatFractionTotal: function (number) {
            return number < 10 ? '0' + number : number;
          },
        }"
        :navigation="{
          prevEl: `#${id}-prev`,
          nextEl: `#${id}-next`,
        }"
        class="gallery__swiper"
        data-aos="fade-up"
        data-aos-easing="ease-out-cubic"
        @swiper="onSwiper"
        @lock="onLockToggle"
        @unlock="onLockToggle"
      >
        <SwiperSlide
          v-for="(item, index) in items"
          :key="index"
          :class="['gallery__item', { 'gallery__item--video': item.url }]"
        >
          <a
            v-if="item.src"
            :href="item.src"
            :data-pswp-width="item.width"
            :data-pswp-height="item.height"
            class="gallery__item-link"
            :style="`aspect-ratio: ${item.width / item.height}`"
          >
            <NuxtImg
              :src="item.src"
              :alt="item.alt"
              class="gallery__item-image"
              loading="lazy"
            />
          </a>
          <VideoPlayer
            v-else-if="item.url"
            :url="item.url"
            :preview="item.preview"
            :title="item.title"
            :view="'carousel'"
            class="gallery__item-video-player"
          />
        </SwiperSlide>
      </Swiper>
      <div
        class="gallery__bottom"
        :class="{ 'gallery__bottom--with-buttons': hasModeButtons }"
        data-aos="fade-up"
        data-aos-easing="ease-out-cubic"
      >
        <div
          v-show="!isLocked"
          :id="`${id}-pagination`"
          :class="[
            'swiper-pagination gallery__pagination',
            backgroundColor === 'black' ? 'gallery__pagination--white' : '',
          ]"
        ></div>
        <slot name="mode-buttons" />
        <div v-show="!isLocked" class="swiper-navigation gallery__navigation">
          <Button
            :id="`${id}-prev`"
            type="button"
            rounded
            variant="outlined"
            class="gallery__navigation-prev button-prev js-button-gallery-prev"
          >
            <CIcon
              class="button-prev__icon"
              :name="
                backgroundColor === 'black'
                  ? 'direction-white'
                  : 'direction-black'
              "
              width="16"
              height="16"
            />
          </Button>
          <Button
            :id="`${id}-next`"
            type="button"
            rounded
            variant="outlined"
            class="gallery__navigation-next button-next js-button-gallery-next"
          >
            <CIcon
              class="button-next__icon"
              :name="
                backgroundColor === 'black'
                  ? 'direction-white'
                  : 'direction-black'
              "
              width="16"
              height="16"
            />
          </Button>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
.gallery {
  $parent: &;
  width: 100%;

  &__swiper {
    width: 100%;
    height: 256px;

    @include media-breakpoint-up(sm) {
      height: 286px;
    }
    @include media-breakpoint-up(md) {
      height: 480px;
      overflow: hidden;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(height, 480);
    }
  }

  &__item {
    width: auto;
    height: 100%;
    display: block;

    &-link {
      display: block;
      height: 100%;
      width: auto;
    }

    &-image {
      display: block;
      height: 100%;
      width: 100%;
      object-fit: cover;
      border-radius: 8px;

      @include media-breakpoint-up(md) {
        border-radius: 4px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-radius, 4);
      }
    }

    &--video {
      aspect-ratio: 16 / 9;
      height: 100%;
      width: auto;
    }
  }

  &__item-video-player {
    height: 100%;
    width: 100%;
  }
  &__navigation {
    padding: 0;
  }
  &__bottom {
    display: grid;
    gap: 40px;
    grid-template-columns: 1fr;
    padding-top: 40px;
    &--with-buttons {
      padding-top: 0px;
    }
    @include media-breakpoint-up(sm) {
      align-items: center;
      gap: 16px;
      justify-content: space-between;
      grid-template-columns: 108px 108px;
      padding-top: 56px;
      &--with-buttons {
        grid-template-columns: 108px auto 108px;
      }
    }
    @include media-breakpoint-up(md) {
      align-items: end;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 16);
      @include responsive-spacing(padding-top, 56);
      grid-template-columns: px-to-vw(108) px-to-vw(108);
      &--with-buttons {
        grid-template-columns: px-to-vw(108) auto px-to-vw(108);
      }
    }
  }
  &--without-fraction {
    #{$parent} {
      &__bottom {
        grid-template-columns: 1fr;
        padding-top: 40px;
        @include media-breakpoint-up(sm) {
          padding-top: 56px;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(padding-top, 56);
        }
      }
      &__pagination {
        @include media-breakpoint-up(sm) {
          display: none;
        }
      }
    }
  }
}
</style>
